import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';

export interface Comment {
  id: string;
  userId: string;
  username: string;
  userPhotoURL: string;
  content: string;
  createdAt: string;
}

export interface Post {
  id: string;
  userId: string;
  username: string;
  userPhotoURL: string;
  subject: string;
  photo: string;
  content: string;
  createdAt: string;
  likeCount: number;
  commentCount: number;
  isLiked: boolean;
  isSaved: boolean;
  factRating: 'high' | 'medium' | 'low';
  topic?: string;
  comments: Comment[];
}

interface CreatePostData {
  userId: string;
  username: string;
  userPhotoURL: string;
  subject: string;
  photo: string;
  content: string;
  factRating: 'high' | 'medium' | 'low';
  topic: string;
}

interface PostContextType {
  posts: Post[];
  savedPosts: Post[];
  isLoading: boolean;
  createPost: (postData: CreatePostData) => Promise<void>;
  editPost: (postId: string, updates: Partial<Post>) => Promise<void>;
  deletePost: (postId: string) => Promise<void>;
  likePost: (postId: string) => Promise<void>;
  savePost: (postId: string) => Promise<void>;
  addComment: (postId: string, comment: { userId: string; username: string; userPhotoURL: string; content: string }) => Promise<void>;
  deleteComment: (postId: string, commentId: string) => Promise<void>;
  getUserPosts: (userId: string) => Post[];
}

const PostContext = createContext<PostContextType | undefined>(undefined);

export const PostProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { currentUser, isLoading: authLoading } = useAuth();
  const [posts, setPosts] = useState<Post[]>([]);
  const [savedPosts, setSavedPosts] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Fetch posts from Supabase
  const fetchPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('posts')
        .select(`
          id,
          user_id,
          subject,
          content,
          photo_url,
          created_at,
          fact_rating,
          topic,
          fact_rating_votes(*),
          profiles!posts_user_id_fkey(username, photo_url)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch likes and comments for all posts
      const postIds = data.map(p => p.id);

      // Get like counts and check if current user liked
      const { data: likesData } = await supabase
        .from('likes')
        .select('post_id, user_id')
        .in('post_id', postIds);

      // Get comment counts
      const { data: commentsData } = await supabase
        .from('comments')
        .select('post_id, id, user_id, content, created_at, profiles!comments_user_id_fkey(username, photo_url)')
        .in('post_id', postIds)
        .order('created_at', { ascending: true });

      // Get saved posts for current user
      let savedPostIds: string[] = [];
      if (currentUser) {
        const { data: savedData } = await supabase
          .from('saved_posts')
          .select('post_id')
          .eq('user_id', currentUser.id);
        savedPostIds = savedData?.map(s => s.post_id) || [];
      }

      const formattedPosts: Post[] = data.map(post => {
        // Check for blob URLs and log them
        if (post.photo_url && post.photo_url.startsWith('blob:')) {
          console.warn(`Post ${post.id} has blob URL: ${post.photo_url}`);
        }

        // Count likes for this post
        const postLikes = likesData?.filter(l => l.post_id === post.id) || [];
        const likeCount = postLikes.length;
        const isLiked = currentUser ? postLikes.some(l => l.user_id === currentUser.id) : false;

        // Get comments for this post
        const postComments = commentsData?.filter(c => c.post_id === post.id) || [];
        const comments: Comment[] = postComments.map(c => ({
          id: c.id,
          userId: c.user_id,
          username: c.profiles?.username || 'Unknown User',
          userPhotoURL: c.profiles?.photo_url || 'https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg',
          content: c.content,
          createdAt: c.created_at
        }));

        return {
          id: post.id,
          userId: post.user_id,
          username: post.profiles?.username || 'Unknown User',
          userPhotoURL: post.profiles?.photo_url || 'https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg',
          subject: post.subject,
          photo: post.photo_url,
          content: post.content,
          createdAt: post.created_at,
          likeCount,
          commentCount: comments.length,
          isLiked,
          isSaved: savedPostIds.includes(post.id),
          factRating: post.fact_rating,
          topic: post.topic,
          comments
        };
      });

      setPosts(formattedPosts);
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
  };

  // Fetch saved posts
  const fetchSavedPosts = async () => {
    if (!currentUser) return;

    try {
      const { data, error } = await supabase
        .from('saved_posts')
        .select(`
          posts(
            id,
            user_id,
            subject,
            content,
            photo_url,
            created_at,
            fact_rating,
            topic,
            profiles!posts_user_id_fkey(username, photo_url)
          )
        `)
        .eq('user_id', currentUser.id);

      if (error) throw error;

      const formattedSavedPosts: Post[] = data
        .filter(item => item.posts)
        .map(item => {
          const post = item.posts;
          return {
            id: post.id,
            userId: post.user_id,
            username: post.profiles?.username || 'Unknown User',
            userPhotoURL: post.profiles?.photo_url || 'https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg',
            subject: post.subject,
            photo: post.photo_url,
            content: post.content,
            createdAt: post.created_at,
            likeCount: 0,
            commentCount: 0,
            isLiked: false,
            isSaved: true,
            factRating: post.fact_rating,
            topic: post.topic,
            comments: []
          };
        });

      setSavedPosts(formattedSavedPosts);
    } catch (error) {
      console.error('Error fetching saved posts:', error);
    }
  };

  useEffect(() => {
    // Wait for auth to complete before fetching posts
    if (authLoading) {
      return;
    }

    // Only fetch once after auth is complete
    if (!hasInitialized) {
      setHasInitialized(true);
      fetchPosts();
      if (currentUser) {
        fetchSavedPosts();
      }
    } else {
      // If user changes (login/logout), refetch
      fetchPosts();
      if (currentUser) {
        fetchSavedPosts();
      } else {
        setSavedPosts([]);
      }
    }
  }, [currentUser, authLoading, hasInitialized]);

  const createPost = async (postData: CreatePostData) => {
    if (!currentUser) throw new Error('User not authenticated');

    // Validate that photo is not a blob URL
    if (postData.photo.startsWith('blob:')) {
      throw new Error('Image upload not completed. Please try uploading the image again.');
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('posts')
        .insert({
          user_id: postData.userId,
          subject: postData.subject,
          content: postData.content,
          photo_url: postData.photo,
          fact_rating: postData.factRating,
          topic: postData.topic
        })
        .select()
        .single();

      if (error) throw error;

      // Add the new post to the local state
      const newPost: Post = {
        id: data.id,
        userId: data.user_id,
        username: postData.username,
        userPhotoURL: postData.userPhotoURL,
        subject: data.subject,
        photo: data.photo_url,
        content: data.content,
        createdAt: data.created_at,
        likeCount: 0,
        commentCount: 0,
        isLiked: false,
        isSaved: false,
        factRating: data.fact_rating,
        topic: data.topic,
        comments: []
      };

      setPosts(prev => [newPost, ...prev]);
    } catch (error) {
      console.error('Error creating post:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const editPost = async (postId: string, updates: Partial<Post>) => {
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('posts')
        .update({
          subject: updates.subject,
          content: updates.content,
          photo_url: updates.photo,
          topic: updates.topic,
          updated_at: new Date().toISOString()
        })
        .eq('id', postId);

      if (error) throw error;

      // Update local state
      setPosts(prev => prev.map(post => 
        post.id === postId ? { ...post, ...updates } : post
      ));
    } catch (error) {
      console.error('Error editing post:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deletePost = async (postId: string) => {
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('posts')
        .delete()
        .eq('id', postId);

      if (error) throw error;

      // Remove from local state
      setPosts(prev => prev.filter(post => post.id !== postId));
      setSavedPosts(prev => prev.filter(post => post.id !== postId));
    } catch (error) {
      console.error('Error deleting post:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const likePost = async (postId: string) => {
    if (!currentUser) return;

    try {
      const post = posts.find(p => p.id === postId);
      if (!post) return;

      if (post.isLiked) {
        // Unlike the post
        const { error } = await supabase
          .from('likes')
          .delete()
          .eq('user_id', currentUser.id)
          .eq('post_id', postId);

        if (error) throw error;

        // Update local state
        setPosts(prev => prev.map(p =>
          p.id === postId
            ? { ...p, isLiked: false, likeCount: Math.max(0, p.likeCount - 1) }
            : p
        ));
      } else {
        // Like the post
        const { error } = await supabase
          .from('likes')
          .insert({
            user_id: currentUser.id,
            post_id: postId
          });

        if (error) throw error;

        // Update local state
        setPosts(prev => prev.map(p =>
          p.id === postId
            ? { ...p, isLiked: true, likeCount: p.likeCount + 1 }
            : p
        ));
      }
    } catch (error) {
      console.error('Error liking post:', error);
      throw error;
    }
  };

  const savePost = async (postId: string) => {
    if (!currentUser) return;

    try {
      const post = posts.find(p => p.id === postId);
      if (!post) return;

      if (post.isSaved) {
        // Unsave post
        const { error } = await supabase
          .from('saved_posts')
          .delete()
          .eq('user_id', currentUser.id)
          .eq('post_id', postId);

        if (error) throw error;

        setSavedPosts(prev => prev.filter(p => p.id !== postId));
        setPosts(prev => prev.map(p => 
          p.id === postId ? { ...p, isSaved: false } : p
        ));
      } else {
        // Save post
        const { error } = await supabase
          .from('saved_posts')
          .insert({
            user_id: currentUser.id,
            post_id: postId
          });

        if (error) throw error;

        setSavedPosts(prev => [...prev, { ...post, isSaved: true }]);
        setPosts(prev => prev.map(p => 
          p.id === postId ? { ...p, isSaved: true } : p
        ));
      }
    } catch (error) {
      console.error('Error saving post:', error);
    }
  };

  const addComment = async (postId: string, comment: { userId: string; username: string; userPhotoURL: string; content: string }) => {
    if (!currentUser) return;

    try {
      const { data, error } = await supabase
        .from('comments')
        .insert({
          post_id: postId,
          user_id: comment.userId,
          content: comment.content
        })
        .select()
        .single();

      if (error) throw error;

      // Create the new comment object
      const newComment: Comment = {
        id: data.id,
        userId: comment.userId,
        username: comment.username,
        userPhotoURL: comment.userPhotoURL,
        content: comment.content,
        createdAt: data.created_at
      };

      // Update local state
      setPosts(prev => prev.map(p =>
        p.id === postId
          ? {
              ...p,
              comments: [...p.comments, newComment],
              commentCount: p.commentCount + 1
            }
          : p
      ));
    } catch (error) {
      console.error('Error adding comment:', error);
      throw error;
    }
  };

  const deleteComment = async (postId: string, commentId: string) => {
    if (!currentUser) return;

    try {
      const { error } = await supabase
        .from('comments')
        .delete()
        .eq('id', commentId)
        .eq('user_id', currentUser.id);

      if (error) throw error;

      // Update local state
      setPosts(prev => prev.map(p =>
        p.id === postId
          ? {
              ...p,
              comments: p.comments.filter(c => c.id !== commentId),
              commentCount: Math.max(0, p.commentCount - 1)
            }
          : p
      ));
    } catch (error) {
      console.error('Error deleting comment:', error);
      throw error;
    }
  };

  const getUserPosts = (userId: string) => {
    return posts.filter(post => post.userId === userId);
  };

  const contextValue: PostContextType = {
    posts,
    savedPosts,
    isLoading,
    createPost,
    editPost,
    deletePost,
    likePost,
    savePost,
    addComment,
    deleteComment,
    getUserPosts
  };

  return (
    <PostContext.Provider value={contextValue}>
      {children}
    </PostContext.Provider>
  );
};

export const usePosts = () => {
  const context = useContext(PostContext);
  if (context === undefined) {
    throw new Error('usePosts must be used within a PostProvider');
  }
  return context;
};